// Admin Module: Validation View
// Legal/KYC/Tasks validation status view

import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Alert,
    Stack,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import {
    CheckCircle,
    Warning,
    Error,
    Schedule,
    Visibility,
    Assignment,
    Gavel,
    Security,
    Task,
} from "@mui/icons-material";
import { opportunitiesService } from "../../opportunities/opportunitiesService";

const ValidationView = () => {

    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [detailDialog, setDetailDialog] = useState({ open: false, opportunity: null });

    useEffect(() => {
        loadOpportunities();
    }, []);

    const loadOpportunities = async () => {
        try {
            setLoading(true);
            const data = await opportunitiesService.getActiveOpportunities();
            setOpportunities(data);
        } catch (error) {
            setError("Error al cargar las oportunidades");
        } finally {
            setLoading(false);
        }
    };

    const getValidationStatus = (_opportunity) => {
    // Mock validation status - in real app this would come from database
        const statuses = {
            kyc: Math.random() > 0.3 ? "completed" : Math.random() > 0.5 ? "pending" : "rejected",
            legal: Math.random() > 0.4 ? "completed" : Math.random() > 0.6 ? "pending" : "rejected",
            tasks: Math.random() > 0.2 ? "completed" : Math.random() > 0.7 ? "pending" : "rejected",
            documents: Math.random() > 0.3 ? "completed" : Math.random() > 0.5 ? "pending" : "rejected",
        };

        const total = Object.keys(statuses).length;
        const completed = Object.values(statuses).filter(s => s === "completed").length;
        const pending = Object.values(statuses).filter(s => s === "pending").length;
        const rejected = Object.values(statuses).filter(s => s === "rejected").length;

        return {
            statuses,
            total,
            completed,
            pending,
            rejected,
            percentage: (completed / total) * 100,
        };
    };

    const getStatusColor = (status) => {
        switch (status) {
        case "completed": return "success";
        case "pending": return "warning";
        case "rejected": return "error";
        default: return "default";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
        case "completed": return <CheckCircle color="success" />;
        case "pending": return <Schedule color="warning" />;
        case "rejected": return <Error color="error" />;
        default: return <Warning color="default" />;
        }
    };

    const handleViewDetails = (opportunity) => {
        setDetailDialog({ open: true, opportunity });
    };

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography>Cargando validaciones...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Validation Overview */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <Security color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">KYC</Typography>
                            </Box>
                            <Typography variant="h4" color="primary">
                                {opportunities.filter(opp => getValidationStatus(opp).statuses.kyc === "completed").length}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                Completados de {opportunities.length} total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <Gavel color="secondary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Legal</Typography>
                            </Box>
                            <Typography variant="h4" color="secondary">
                                {opportunities.filter(opp => getValidationStatus(opp).statuses.legal === "completed").length}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                Completados de {opportunities.length} total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <Task color="warning" sx={{ mr: 1 }} />
                                <Typography variant="h6">Tareas</Typography>
                            </Box>
                            <Typography variant="h4" color="warning.main">
                                {opportunities.filter(opp => getValidationStatus(opp).statuses.tasks === "completed").length}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                Completados de {opportunities.length} total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <Assignment color="info" sx={{ mr: 1 }} />
                                <Typography variant="h6">Documentos</Typography>
                            </Box>
                            <Typography variant="h4" color="info.main">
                                {opportunities.filter(opp => getValidationStatus(opp).statuses.documents === "completed").length}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                Completados de {opportunities.length} total
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Validation Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
            Estado de Validación por Oportunidad
                    </Typography>
          
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Oportunidad</TableCell>
                                    <TableCell>KYC</TableCell>
                                    <TableCell>Legal</TableCell>
                                    <TableCell>Tareas</TableCell>
                                    <TableCell>Documentos</TableCell>
                                    <TableCell>Progreso</TableCell>
                                    <TableCell>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {opportunities.map((opportunity) => {
                                    const validation = getValidationStatus(opportunity);
                                    return (
                                        <TableRow key={opportunity.id} hover>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                                        {opportunity.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {opportunity.location}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {getStatusIcon(validation.statuses.kyc)}
                                                    <Chip
                                                        label={validation.statuses.kyc}
                                                        color={getStatusColor(validation.statuses.kyc)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {getStatusIcon(validation.statuses.legal)}
                                                    <Chip
                                                        label={validation.statuses.legal}
                                                        color={getStatusColor(validation.statuses.legal)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {getStatusIcon(validation.statuses.tasks)}
                                                    <Chip
                                                        label={validation.statuses.tasks}
                                                        color={getStatusColor(validation.statuses.tasks)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {getStatusIcon(validation.statuses.documents)}
                                                    <Chip
                                                        label={validation.statuses.documents}
                                                        color={getStatusColor(validation.statuses.documents)}
                                                        size="small"
                                                    />
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                <Box sx={{ width: 100 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={validation.percentage}
                                                        color={validation.percentage === 100 ? "success" : "primary"}
                                                        sx={{ height: 8, borderRadius: 4 }}
                                                    />
                                                    <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                                                        {Math.round(validation.percentage)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                      
                                            <TableCell>
                                                <Tooltip title="Ver detalles de validación">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(opportunity)}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog
                open={detailDialog.open}
                onClose={() => setDetailDialog({ open: false, opportunity: null })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
          Detalles de Validación - {detailDialog.opportunity?.title}
                </DialogTitle>
                <DialogContent>
                    {detailDialog.opportunity && (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                <Security />
                        Estado KYC
                                            </Typography>
                                            <Stack spacing={2}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography>Verificación de identidad</Typography>
                                                    <Chip label="Completado" color="success" size="small" />
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography>Verificación financiera</Typography>
                                                    <Chip label="Pendiente" color="warning" size="small" />
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography>Documentación</Typography>
                                                    <Chip label="Completado" color="success" size="small" />
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                <Gavel />
                        Estado Legal
                                            </Typography>
                                            <Stack spacing={2}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography>Revisión de contratos</Typography>
                                                    <Chip label="Completado" color="success" size="small" />
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography>Cumplimiento normativo</Typography>
                                                    <Chip label="Pendiente" color="warning" size="small" />
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography>Certificaciones</Typography>
                                                    <Chip label="Completado" color="success" size="small" />
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                <Task />
                        Tareas del Proyecto
                                            </Typography>
                                            <Stack spacing={2}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <Typography>Análisis de mercado</Typography>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={75}
                                                            sx={{ width: 100, height: 6 }}
                                                        />
                                                        <Typography variant="caption">75%</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <Typography>Evaluación de riesgos</Typography>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={100}
                                                            color="success"
                                                            sx={{ width: 100, height: 6 }}
                                                        />
                                                        <Typography variant="caption">100%</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <Typography>Plan financiero</Typography>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={50}
                                                            color="warning"
                                                            sx={{ width: 100, height: 6 }}
                                                        />
                                                        <Typography variant="caption">50%</Typography>
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialog({ open: false, opportunity: null })}>
            Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ValidationView; 